import { createClient } from '@supabase/supabase-js';
import type { Database } from '../integrations/supabase/types';

declare global {
  interface ImportMeta {
    env: {
      VITE_SUPABASE_URL: string;
      VITE_SUPABASE_ANON_KEY: string;
    }
  }
}

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your environment variables.'
  );
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: {
      getItem: (key) => {
        try {
          const storedSession = localStorage.getItem(key);
          console.log('Getting session from storage:', key, storedSession ? 'Found' : 'Not found');
          return storedSession;
        } catch (error) {
          console.error('Error getting session from storage:', error);
          return null;
        }
      },
      setItem: (key, value) => {
        try {
          console.log('Setting session in storage:', key);
          localStorage.setItem(key, value);
        } catch (error) {
          console.error('Error setting session in storage:', error);
        }
      },
      removeItem: (key) => {
        try {
          console.log('Removing session from storage:', key);
          localStorage.removeItem(key);
        } catch (error) {
          console.error('Error removing session from storage:', error);
        }
      }
    },
    storageKey: 'supabase.auth.token',
  },
  global: {
    headers: {
      'X-Client-Info': 'lovable',
    },
  },
});

// Initialize auth state with better error handling and logging
supabase.auth.onAuthStateChange((event, session) => {
  console.log("Auth state change event:", event);
  
  if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
    const storage = supabase.auth.storage;
    storage?.removeItem('supabase.auth.token');
    window.location.href = '/sign-in';
  } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
    if (session) {
      const storage = supabase.auth.storage;
      const sessionStr = JSON.stringify(session);
      storage?.setItem('supabase.auth.token', sessionStr);
      console.log('Session stored successfully');
    }
  }
});

// Add a function to check and refresh session if needed
export const refreshSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    
    if (session) {
      const { data: { session: refreshedSession }, error: refreshError } = 
        await supabase.auth.refreshSession();
      
      if (refreshError) throw refreshError;
      
      if (refreshedSession) {
        console.log('Session refreshed successfully');
        return refreshedSession;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error refreshing session:', error);
    return null;
  }
};